/**
 * AN-ACC Assessment Data Model
 * Based on Australian National Aged Care Classification specifications
 */

export const createANACCAssessment = (data = {}) => {
  return {
    id: data.id || null,
    residentId: data.residentId || null,
    
    // Assessment Type
    assessmentType: data.assessmentType || 'shadow', // shadow, official
    assessmentDate: data.assessmentDate || new Date().toISOString(),
    assessedBy: data.assessedBy || null,
    
    // Physical Function (RUG-ADL)
    rugAdl: {
      bedMobility: data.rugAdl?.bedMobility || null, // Score 1-5
      toileting: data.rugAdl?.toileting || null, // Score 1-5
      transfer: data.rugAdl?.transfer || null, // Score 1-5
      eating: data.rugAdl?.eating || null // Score 1-5
    },
    rugAdlTotal: data.rugAdlTotal || null, // Sum of scores
    
    // Frailty & Pressure Care
    rockwoodFrailtyScale: data.rockwoodFrailtyScale || null, // 1-9 (Very Fit to Terminally Ill)
    bradenScale: data.bradenScale || null, // Integer for pressure sore risk
    
    // Mobility
    demmiScore: data.demmiScore || null, // 0-100 (De Morton Mobility Index)
    
    // Technical Nursing Requirements
    oxygenTherapy: data.oxygenTherapy ?? false, // Daily usage
    enteralFeeding: data.enteralFeeding ?? false,
    enteralFeedingDetails: data.enteralFeedingDetails || {
      formula: null,
      deliveryMethod: null,
      frequency: null
    },
    complexWoundManagement: data.complexWoundManagement ?? false,
    woundCareModuleLink: data.woundCareModuleLink || null,
    
    // Additional Clinical Factors
    cognitiveStatus: data.cognitiveStatus || null,
    behaviouralSymptoms: data.behaviouralSymptoms || null,
    continence: data.continence || null,
    nutrition: data.nutrition || null,
    
    // AN-ACC Classification
    anaccClass: data.anaccClass || null, // 1-13
    anaccClassDescription: data.anaccClassDescription || null,
    classificationDate: data.classificationDate || null,
    classificationSource: data.classificationSource || null, // shadow, official
    
    // Revenue Forecasting
    projectedDailySubsidy: data.projectedDailySubsidy || null,
    nwcWeighting: data.nwcWeighting || null, // National Weighted Activity Unit
    revenueForecast: data.revenueForecast || null,
    
    // Comparison with Official
    officialClassification: data.officialClassification || null,
    officialClassificationDate: data.officialClassificationDate || null,
    discrepancy: data.discrepancy || null, // If shadow differs from official
    
    // Status
    status: data.status || 'draft', // draft, completed, submitted, official
    
    // Metadata
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
    createdBy: data.createdBy || null
  };
};

export const RUG_ADL_SCORES = {
  INDEPENDENT: 1,
  SUPERVISION: 2,
  LIMITED_ASSISTANCE: 3,
  EXTENSIVE_ASSISTANCE: 4,
  DEPENDENT: 5
};

export const ROCKWOOD_FRAILTY_SCALE = {
  VERY_FIT: 1,
  WELL: 2,
  MANAGING_WELL: 3,
  VULNERABLE: 4,
  MILD_FRAILTY: 5,
  MODERATE_FRAILTY: 6,
  SEVERE_FRAILTY: 7,
  VERY_SEVERE_FRAILTY: 8,
  TERMINALLY_ILL: 9
};

export const ANACC_CLASSES = {
  1: 'Class 1 - Very Low Care Needs',
  2: 'Class 2 - Low Care Needs',
  3: 'Class 3 - Low-Moderate Care Needs',
  4: 'Class 4 - Moderate Care Needs',
  5: 'Class 5 - Moderate-High Care Needs',
  6: 'Class 6 - High Care Needs',
  7: 'Class 7 - High-Very High Care Needs',
  8: 'Class 8 - Very High Care Needs',
  9: 'Class 9 - Very High-Extreme Care Needs',
  10: 'Class 10 - Extreme Care Needs',
  11: 'Class 11 - Extreme-Very Extreme Care Needs',
  12: 'Class 12 - Very Extreme Care Needs',
  13: 'Class 13 - Very Extreme-Highest Care Needs'
};

export const calculateRUGADLTotal = (rugAdl) => {
  if (!rugAdl) return null;
  
  const scores = [
    rugAdl.bedMobility,
    rugAdl.toileting,
    rugAdl.transfer,
    rugAdl.eating
  ].filter(score => score !== null && score !== undefined);
  
  if (scores.length === 0) return null;
  
  return scores.reduce((sum, score) => sum + score, 0);
};

export const classifyANACC = (assessment) => {
  // Simplified classification logic
  // In production, this would use the official AN-ACC algorithm
  // This is a placeholder for the actual classification logic
  
  const rugAdlTotal = calculateRUGADLTotal(assessment.rugAdl);
  const frailty = assessment.rockwoodFrailtyScale;
  const demmi = assessment.demmiScore;
  const technicalNursing = assessment.oxygenTherapy || 
                           assessment.enteralFeeding || 
                           assessment.complexWoundManagement;
  
  // Basic classification logic (simplified)
  let anaccClass = 1;
  
  if (technicalNursing) {
    anaccClass += 3;
  }
  
  if (frailty && frailty >= 7) {
    anaccClass += 2;
  }
  
  if (rugAdlTotal && rugAdlTotal >= 15) {
    anaccClass += 2;
  }
  
  if (demmi && demmi < 30) {
    anaccClass += 1;
  }
  
  // Cap at 13
  anaccClass = Math.min(anaccClass, 13);
  
  return {
    anaccClass,
    anaccClassDescription: ANACC_CLASSES[anaccClass] || 'Unknown',
    classificationSource: 'shadow'
  };
};

